// src/pages/Admin/LiveStreams/GoLivePage.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Radio,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Settings,
  X,
  AlertCircle,
  CheckCircle,
  Eye,
  Users,
  Square,
  Monitor,
  MonitorOff,
  Circle,
  Download
} from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import { useWebRTC } from '../../../hooks/useWebRTC';
import liveStreamService, { AGE_RATINGS, type AgeRatingValue } from '../../../services/liveStreamService';
import type { LiveStream } from '../../../services/liveStreamService';

const GoLivePage: React.FC = () => {
  const navigate = useNavigate();
  const { streamId } = useParams<{ streamId: string }>();
  const { showError, showSuccess } = useToast();
  
  // Stream state
  const [stream, setStream] = useState<LiveStream | null>(null);
  const [pendingRating, setPendingRating] = useState<AgeRatingValue>('15');
  const [savingRating, setSavingRating] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'live'>('disconnected');
  const [showEndStreamModal, setShowEndStreamModal] = useState(false);
  
  // Media state
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [autoRecord, setAutoRecord] = useState(true); // Auto-record by default for replays
  
  // Stats
  const [duration, setDuration] = useState(0);
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null); // Ref for reliable cleanup

  // WebRTC hook for broadcasting
  // Memoised, because these are passed into useWebRTC and a new identity on every
  // render used to rebuild its WebSocket client on every render - a loop that opened one
  // socket per iteration until the browser ran out. The hook holds its callbacks in a ref
  // now so this can no longer cause that, but passing inline arrows into a hook that
  // builds a connection is the habit that caused it, so it is not reinstated here.
  const handleWebRTCConnectionState = useCallback((state: RTCPeerConnectionState) => {
    if (state === 'connected') {
      setConnectionStatus('connected');
    } else if (state === 'failed' || state === 'disconnected') {
      setConnectionStatus('disconnected');
    }
  }, []);

  const handleWebRTCError = useCallback((error: Error) => {
    showError(`WebRTC error: ${error.message}`);
  }, [showError]);

  const {
    isConnected: webrtcConnected,
    isBroadcasting,
    viewerCount,
    isScreenSharing,
    isRecording,
    startBroadcast,
    stopBroadcast,
    startScreenShare,
    stopScreenShare,
    startRecording,
    stopRecording,
    downloadRecording,
  } = useWebRTC({
    streamId: streamId || '',
    isBroadcaster: true,
    onConnectionStateChange: handleWebRTCConnectionState,
    onError: handleWebRTCError,
  });

  // Load stream data if streamId is provided
  useEffect(() => {
    if (streamId) {
      loadStream(streamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const loadStream = async (id: string) => {
    try {
      const streamData = await liveStreamService.getStreamById(id);
      if (streamData) {
        setStream(streamData);
        if (streamData.status === 'LIVE') {
          setIsLive(true);
          setConnectionStatus('live');
        }
      }
    } catch (error) {
      console.error('Error loading stream:', error);
      showError('Failed to load stream data');
    }
  };

  // Initialize media devices
  const initializeMedia = useCallback(async () => {
    try {
      setMediaError(null);
      
      // Request camera and microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'user'
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      setMediaStream(stream);
      mediaStreamRef.current = stream; // Store in ref for cleanup
      
      // Attach to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error: unknown) {
      console.error('Media access error:', error);
      const err = error as { name?: string };
      
      if (err.name === 'NotAllowedError') {
        setMediaError('Camera and microphone access was denied. Please allow access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setMediaError('No camera or microphone found. Please connect a device and try again.');
      } else {
        setMediaError('Failed to access camera/microphone. Please check your device settings.');
      }
    }
  }, []);

  useEffect(() => {
    initializeMedia();
    
    return () => {
      // Cleanup intervals on unmount
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      // Cleanup media stream on unmount using ref
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update video preview when camera is toggled
  useEffect(() => {
    if (mediaStream) {
      mediaStream.getVideoTracks().forEach(track => {
        track.enabled = cameraEnabled;
      });
    }
  }, [cameraEnabled, mediaStream]);

  // Update audio when mic is toggled
  useEffect(() => {
    if (mediaStream) {
      mediaStream.getAudioTracks().forEach(track => {
        track.enabled = micEnabled;
      });
    }
  }, [micEnabled, mediaStream]);

  const toggleCamera = () => {
    setCameraEnabled(!cameraEnabled);
  };

  const toggleMic = () => {
    setMicEnabled(!micEnabled);
  };

  const handleToggleScreenShare = async () => {
    try {
      if (isScreenSharing) {
        stopScreenShare();
        showSuccess('Screen sharing stopped');
      } else {
        await startScreenShare();
        showSuccess('Screen sharing started');
      }
    } catch (error) {
      console.error('Screen share error:', error);
      showError('Failed to toggle screen sharing');
    }
  };

  const handleToggleRecording = async () => {
    try {
      if (isRecording) {
        const blob = await stopRecording();
        setRecordedBlob(blob);
        showSuccess('Recording stopped - ready to download');
      } else {
        startRecording();
        setRecordedBlob(null);
        showSuccess('Recording started');
      }
    } catch (error) {
      console.error('Recording error:', error);
      showError('Failed to toggle recording');
    }
  };

  const handleDownloadRecording = () => {
    if (!recordedBlob) {
      showError('No recording available');
      return;
    }
    try {
      downloadRecording(recordedBlob, `${stream?.title || 'livestream'}_${new Date().toISOString().split('T')[0]}.webm`);
      showSuccess('Recording downloaded');
    } catch (error) {
      console.error('Download error:', error);
      showError('Failed to download recording');
    }
  };

  const handleGoLive = async () => {
    if (!stream) {
      showError('No stream data available');
      return;
    }

    if (!mediaStream) {
      showError('Camera/microphone not available');
      return;
    }

    setIsConnecting(true);
    setConnectionStatus('connecting');
    
    try {
      // Start the stream on the backend
      await liveStreamService.startStream(stream.id);
      
      // Start WebRTC broadcasting with the local media stream
      await startBroadcast(mediaStream);
      
      setConnectionStatus('live');
      setIsLive(true);
      showSuccess('🔴 You are now LIVE! Broadcasting via WebRTC');
      
      // Auto-start recording if enabled (for replays)
      if (autoRecord) {
        // Small delay to ensure localStreamRef is fully set
        setTimeout(() => {
          try {
            startRecording();
            showSuccess('📹 Recording started automatically for replay');
          } catch (recordError) {
            console.error('Failed to auto-start recording:', recordError);
            showError('⚠️ Auto-recording failed to start. You can manually start recording using the Record button.');
          }
        }, 500);
      }
      
      // Start duration counter
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
      // Reload stream data to get updated info
      await loadStream(stream.id);
      
    } catch (error) {
      // The server's own words. "An age rating is required before going live" tells the
      // host what to do; "Failed to go live. Please try again." sends them round the same
      // loop, which is how this one went unexplained until the console was read.
      console.error('Error starting stream:', error);
      showError(error instanceof Error ? error.message : 'Failed to go live. Please try again.');
      setConnectionStatus('disconnected');
    } finally {
      setIsConnecting(false);
    }
  };

  /**
   * Turns the camera and microphone off.
   *
   * <p>Its own function because it was previously only reachable at the end of a long
   * sequence that could fail before reaching it - so the camera light stayed on and the
   * only way to stop it was closing the tab.
   */
  const releaseCamera = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    setMediaStream(current => {
      current?.getTracks().forEach(track => track.stop());
      return null;
    });
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Leaving the page releases the camera too. Without this the only way to turn the
  // light off was closing the tab, because nothing on the way out stopped the tracks -
  // navigating away from a broadcast left the camera running on the host's machine.
  useEffect(() => releaseCamera, [releaseCamera]);

  /** Gives a stream the rating it needs before it can be started. */
  const handleSaveRating = async () => {
    if (!stream) return;
    setSavingRating(true);
    try {
      await liveStreamService.updateStreamConfig(stream.id, { ageRating: pendingRating });
      await loadStream(stream.id);
      showSuccess(`Rated ${pendingRating}. You can go live now.`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Could not set the age rating');
    } finally {
      setSavingRating(false);
    }
  };

  /**
   * Releases the camera, then ends the stream, then uploads what was captured.
   *
   * <p>That order is the fix, not an accident. This used to upload the thumbnail and the
   * recording first and call endStream afterwards, with the camera released after that -
   * so anything that went wrong with an upload left the stream LIVE in the database
   * forever and the camera light on until the tab was closed. A recording upload that
   * hung (which it will, if the backend goes away mid-transfer) blocked both for as long
   * as the socket took to time out, which is why pressing End Stream repeatedly did
   * nothing.
   *
   * <p>Releasing the camera and ending the stream are local and cheap. Uploads are
   * best-effort and may fail; nothing that matters is allowed to depend on them.
   */
  const handleEndStream = async () => {
    if (!stream) return;

    // Captured before anything stops, because it is a frame of the live video.
    let thumbnailFile: File | null = null;
    try {
      const video = videoRef.current;
      if (video && video.videoWidth > 0) {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          const blob = await new Promise<Blob | null>(resolve =>
            canvas.toBlob(resolve, 'image/jpeg', 0.85)
          );
          if (blob) {
            thumbnailFile = new File([blob], `thumbnail-${stream.id}.jpg`, { type: 'image/jpeg' });
          }
        }
      }
    } catch (thumbError) {
      console.error('Error capturing thumbnail:', thumbError);
    }

    let recordingBlobToUpload: Blob | null = null;
    if (isRecording) {
      try {
        recordingBlobToUpload = await stopRecording();
      } catch (recordError) {
        console.error('Error stopping recording:', recordError);
      }
    } else if (recordedBlob) {
      recordingBlobToUpload = recordedBlob;
    }

    // Everything below here must happen whatever went wrong above.
    stopBroadcast();
    releaseCamera();

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }

    let ended = false;
    try {
      await liveStreamService.endStream(stream.id);
      ended = true;
      setIsLive(false);
      setConnectionStatus('disconnected');
    } catch (error) {
      // The camera is already off and the broadcast already stopped, so the host is not
      // still on air - but the server still believes they are, and that is worth saying
      // plainly rather than hiding behind "failed to end stream".
      console.error('Error ending stream:', error);
      showError(error instanceof Error
        ? `Your camera is off, but the server still has this stream marked live: ${error.message}`
        : 'Your camera is off, but the server still has this stream marked live.');
    }

    // Best effort from here. A failure leaves a replay missing, not a stream stuck live.
    if (thumbnailFile) {
      try {
        await liveStreamService.uploadThumbnail(stream.id, thumbnailFile);
      } catch (thumbUploadError) {
        console.error('Error uploading thumbnail:', thumbUploadError);
      }
    }

    let uploadedRecording = false;
    if (recordingBlobToUpload) {
      if (recordingBlobToUpload.size < 1000) {
        showError('The recording was empty, so there is no replay to save.');
      } else {
        try {
          showSuccess(`Uploading recording (${(recordingBlobToUpload.size / 1024 / 1024).toFixed(2)} MB)…`);
          await liveStreamService.uploadRecording(stream.id, recordingBlobToUpload);
          uploadedRecording = true;
        } catch (uploadError) {
          console.error('Error uploading recording:', uploadError);
          showError(uploadError instanceof Error
            ? `The stream ended, but the replay could not be saved: ${uploadError.message}`
            : 'The stream ended, but the replay could not be saved.');
        }
      }
    }

    if (ended) {
      showSuccess(uploadedRecording
        ? 'Stream ended and the replay was saved.'
        : 'Stream ended.');
    }

    setShowEndStreamModal(false);
    navigate('/admin/live-streams');
  };

  // Handler to show the end stream confirmation modal
  const handleEndStreamClick = () => {
    if (!stream) return;
    setShowEndStreamModal(true);
  };

  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = () => {
    switch (connectionStatus) {
      case 'live':
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-red-600 text-white rounded-full text-sm font-medium animate-pulse">
            <span className="w-2 h-2 bg-white rounded-full"></span>
            LIVE
          </span>
        );
      case 'connecting':
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-yellow-500 text-white rounded-full text-sm font-medium">
            <div className="w-2 h-2 border border-white border-t-transparent rounded-full animate-spin"></div>
            Connecting...
          </span>
        );
      case 'connected':
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium">
            <CheckCircle className="w-3 h-3" />
            Ready
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-2 px-3 py-1 bg-gray-500 text-white rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-white rounded-full"></span>
            Offline
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/live-streams')}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Radio className="h-5 w-5 text-red-500" />
                {stream?.title || 'Live Studio'}
              </h1>
              {stream?.description && (
                <p className="text-sm text-gray-400">{stream.description}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {getStatusBadge()}
            
            {isLive && (
              <div className="flex items-center gap-4 text-sm text-gray-300">
                <span className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  {viewerCount}
                </span>
                <span>{formatDuration(duration)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Main Preview Area */}
        <div className="flex-1 p-6">
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video max-h-[70vh] mx-auto">
            {mediaError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 text-center p-8">
                <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold mb-2">Camera Access Required</h3>
                <p className="text-gray-400 mb-4 max-w-md">{mediaError}</p>
                <button
                  onClick={initializeMedia}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-cover ${!cameraEnabled ? 'hidden' : ''}`}
                />
                {!cameraEnabled && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                    <div className="text-center">
                      <VideoOff className="h-16 w-16 text-gray-500 mx-auto mb-2" />
                      <p className="text-gray-400">Camera is off</p>
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Live indicator overlay */}
            {isLive && (
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <span className="flex items-center gap-2 px-3 py-1 bg-red-600 text-white rounded text-sm font-bold animate-pulse">
                  <span className="w-2 h-2 bg-white rounded-full"></span>
                  LIVE
                </span>
                {isScreenSharing && (
                  <span className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded text-sm font-medium">
                    <Monitor className="h-3 w-3" />
                    Screen Sharing
                  </span>
                )}
                {isRecording && (
                  <span className="flex items-center gap-2 px-3 py-1 bg-red-500 text-white rounded text-sm font-medium animate-pulse">
                    <Circle className="h-3 w-3 fill-current" />
                    Recording
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={toggleMic}
              className={`p-4 rounded-full transition-colors ${
                micEnabled
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
              title={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
            >
              {micEnabled ? <Mic className="h-6 w-6" /> : <MicOff className="h-6 w-6" />}
            </button>
            
            <button
              onClick={toggleCamera}
              className={`p-4 rounded-full transition-colors ${
                cameraEnabled
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
              title={cameraEnabled ? 'Turn off camera' : 'Turn on camera'}
            >
              {cameraEnabled ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
            </button>

            {/* Screen Share Button - only when live */}
            {isLive && (
              <button
                onClick={handleToggleScreenShare}
                className={`p-4 rounded-full transition-colors ${
                  isScreenSharing
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
                title={isScreenSharing ? 'Stop screen share' : 'Share screen'}
              >
                {isScreenSharing ? <MonitorOff className="h-6 w-6" /> : <Monitor className="h-6 w-6" />}
              </button>
            )}

            {/* Recording Button - only when live */}
            {isLive && (
              <button
                onClick={handleToggleRecording}
                className={`p-4 rounded-full transition-colors ${
                  isRecording
                    ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
                title={isRecording ? 'Stop recording' : 'Start recording'}
              >
                {isRecording ? <Square className="h-6 w-6" /> : <Circle className="h-6 w-6" />}
              </button>
            )}

            {/* Download Recording Button - only when recording is available */}
            {recordedBlob && !isRecording && (
              <button
                onClick={handleDownloadRecording}
                className="p-4 rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors"
                title="Download recording"
              >
                <Download className="h-6 w-6" />
              </button>
            )}
            
            {!isLive && stream && !stream.ageRating && (
              /* A stream with no rating cannot be started, and until now there was no
                 field anywhere to give it one - so the refusal was unfixable from the
                 screen that produced it. */
              <div
                data-testid="missing-age-rating"
                className="mb-4 w-full rounded-lg bg-amber-900/40 px-4 py-3 text-sm text-amber-100"
              >
                <p className="mb-2">
                  This stream has no age rating, so it cannot go live - an unrated stream
                  is treated as adults-only and nobody would be able to watch it.
                </p>
                <div className="flex items-center gap-2">
                  <label htmlFor="stream-age-rating" className="text-xs">Set a rating</label>
                  <select
                    id="stream-age-rating"
                    value={pendingRating}
                    onChange={(e) => setPendingRating(e.target.value as AgeRatingValue)}
                    className="rounded border border-amber-300/40 bg-transparent px-2 py-1 text-sm"
                  >
                    {AGE_RATINGS.map((r) => (
                      <option key={r} value={r} className="text-gray-900">{r}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void handleSaveRating()}
                    disabled={savingRating}
                    className="rounded bg-amber-200 px-3 py-1 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                  >
                    {savingRating ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            {!isLive ? (
              <button
                onClick={handleGoLive}
                disabled={isConnecting || !!mediaError}
                className="px-8 py-4 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold text-lg transition-colors"
              >
                {isConnecting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Starting...
                  </>
                ) : (
                  <>
                    <Radio className="h-5 w-5" />
                    Go Live
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleEndStreamClick}
                className="px-8 py-4 bg-gray-700 text-white rounded-full hover:bg-gray-600 flex items-center gap-2 font-semibold text-lg transition-colors"
              >
                <Square className="h-5 w-5" />
                End Stream
              </button>
            )}
            
            <button
              className="p-4 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-colors"
              title="Settings"
            >
              <Settings className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Sidebar - Stream Info */}
        <div className="w-80 bg-gray-800 border-l border-gray-700 p-4 overflow-y-auto">
          <h3 className="font-semibold mb-4">Stream Information</h3>
          
          {stream && (
            <div className="space-y-4">
              {/* WebRTC Connection Status */}
              <div className="bg-gray-700 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Connection Status</p>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${webrtcConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                  <span className="text-sm">{webrtcConnected ? 'WebRTC Connected' : 'Connecting...'}</span>
                </div>
                {isBroadcasting && (
                  <p className="text-xs text-green-400 mt-1">Broadcasting to viewers</p>
                )}
              </div>

              {/* Stats when live */}
              {isLive && (
                <div className="bg-gray-700 rounded-lg p-3 space-y-2">
                  <h4 className="text-sm font-medium">Live Stats</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Eye className="h-4 w-4" />
                      <span>{viewerCount} viewers</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <Users className="h-4 w-4" />
                      <span>{stream.peakViewers || 0} peak</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    Duration: {formatDuration(duration)}
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3">
                <h4 className="text-sm font-medium text-blue-400 mb-2">Tips</h4>
                <ul className="text-xs text-gray-300 space-y-1">
                  <li>• Ensure good lighting for best video quality</li>
                  <li>• Use a stable internet connection</li>
                  <li>• Test your audio before going live</li>
                  <li>• Engage with your viewers in chat</li>
                </ul>
              </div>

              {/* Recording Settings */}
              {!isLive && (
                <div className="bg-gray-700 rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-2">Recording Settings</h4>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoRecord}
                      onChange={(e) => setAutoRecord(e.target.checked)}
                      className="w-4 h-4 rounded bg-gray-600 border-gray-500 text-orange-500 focus:ring-orange-500 focus:ring-offset-gray-800"
                    />
                    <div>
                      <span className="text-sm text-white">Auto-record for replay</span>
                      <p className="text-xs text-gray-400">Save stream so viewers can watch later</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Recording Status when live */}
              {isLive && (
                <div className={`rounded-lg p-3 ${isRecording ? 'bg-red-900/30 border border-red-800' : 'bg-gray-700'}`}>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    {isRecording && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
                    Recording Status
                  </h4>
                  <p className="text-xs text-gray-400">
                    {isRecording 
                      ? 'Recording in progress - will be saved when stream ends'
                      : recordedBlob 
                        ? 'Recording ready to upload when stream ends'
                        : 'Not recording - use the record button to start'}
                  </p>
                </div>
              )}

              {/* WebRTC Info */}
              <div className="bg-gray-700 rounded-lg p-3">
                <h4 className="text-sm font-medium mb-2">Browser Streaming</h4>
                <p className="text-xs text-gray-400">
                  This stream uses WebRTC for low-latency peer-to-peer broadcasting directly from your browser. 
                  No external software required!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* End Stream Confirmation Modal */}
      {showEndStreamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <Square className="h-6 w-6 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-white">End Stream?</h3>
            </div>
            
            <p className="text-gray-300 mb-2">
              Are you sure you want to end this live stream?
            </p>
            
            {isRecording && (
              <div className="bg-green-900/30 border border-green-700 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-400 flex items-center gap-2">
                  <Circle className="h-3 w-3 fill-current animate-pulse" />
                  Your recording will be saved for replay
                </p>
              </div>
            )}
            
            <p className="text-gray-400 text-sm mb-6">
              {viewerCount > 0 
                ? `${viewerCount} viewer${viewerCount !== 1 ? 's' : ''} will be disconnected.`
                : 'The stream will be stopped and you will be redirected.'}
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowEndStreamModal(false)}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEndStream}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Square className="h-4 w-4" />
                End Stream
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoLivePage;
